import {Card, Tooltip} from 'platform/components';
import {Box, HStack, Icon, Show, Text, VStack} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';

import i18n from '@dms/i18n';
import {getQuantityWithHandlingUnit} from '@dms/warehouse/shared';

import type {ArticleIssueChannel} from '@prompt-bridge/shared/validations';

import {EMPTY_PLACEHOLDER} from 'shared/web';

const CHANNEL_APPEARANCE = {
  COM: {label: 'page.vehicle.financingAndLeasing.commercial', color: 'palettes.blue.60.100'},
  INS: {label: 'entity.order.labels.insurance', color: 'palettes.blue.40.100'},
  CON: {label: 'aiAssistant.businessCase.recap.states.CONTRACT', color: 'palettes.green.60.100'},
  WAR: {label: 'page.salesSettings.labels.warranty', color: 'palettes.purple.60.100'},
  INT: {label: 'entity.checkout.labels.internal', color: 'palettes.orange.60.100'},
  CLA: {label: 'entity.order.labels.claim', color: 'palettes.red.60.100'},
  DIRECT: {label: 'entity.warehouse.labels.directSale', color: 'palettes.green.50.100'},
  OTHER: {label: 'entity.warehouse.reporting.otherIssues', color: 'palettes.neutral.60.100'},
} as const;

interface StatisticsChannelsProps {
  channels: ArticleIssueChannel[];
  handlingUnitLabel: string;
  historicalBasis: string;
}

export function StatisticsChannels(props: StatisticsChannelsProps) {
  const formatNumber = useFormatNumber();
  const visibleChannels = props.channels.filter((channel) => channel.netQuantity !== 0);
  const maximum = Math.max(
    0,
    ...visibleChannels.map((channel) => Math.abs(channel.netQuantity ?? 0))
  );
  const isComplete = props.channels.every((channel) => channel.netQuantity !== null);
  const total = isComplete
    ? props.channels.reduce((sum, channel) => sum + (channel.netQuantity ?? 0), 0)
    : null;
  const formatQuantity = (value: number) =>
    getQuantityWithHandlingUnit(value, props.handlingUnitLabel);
  const formatPercent = (value: number | null) =>
    value === null ? EMPTY_PLACEHOLDER : (formatNumber(value, 1) ?? EMPTY_PLACEHOLDER) + ' %';

  return (
    <Card title={i18n.t('entity.warehouse.reporting.issuesBreakdown')} isExpandable>
      <VStack spacing={3}>
        <Text color="secondary">{props.historicalBasis}</Text>
        <HStack spacing={1} align="center">
          <Text color="secondary">{i18n.t('entity.warehouse.reporting.annualPartUsage')}</Text>
          <Tooltip description={i18n.t('entity.warehouse.reporting.annualPartUsageTooltip')}>
            <Icon value="action/help" color="text.secondary" size={4} />
          </Tooltip>
          <Text>{total === null ? EMPTY_PLACEHOLDER : formatQuantity(total)}</Text>
        </HStack>
        <Show when={!isComplete}>
          <Text>{i18n.t('entity.warehouse.reporting.inventoryTurnoverIncompleteSeries')}</Text>
        </Show>
        <Show when={isComplete && visibleChannels.length === 0}>
          <Text>{i18n.t('entity.warehouse.reporting.noIssueHistory')}</Text>
        </Show>
        {visibleChannels.map((channel) => (
          <VStack key={channel.channel} spacing={1}>
            <HStack justify="space-between" align="center" wrap>
              <Text>{i18n.t(CHANNEL_APPEARANCE[channel.channel].label)}</Text>
              <HStack spacing={3} align="center">
                <Text>
                  {channel.netQuantity === null
                    ? EMPTY_PLACEHOLDER
                    : formatQuantity(channel.netQuantity)}
                </Text>
                <Text color="secondary">{formatPercent(channel.sharePercent)}</Text>
              </HStack>
            </HStack>
            <Box
              width="100%"
              height={3}
              backgroundColor="palettes.neutral.10.100"
              borderRadius="small"
            >
              <HStack justify={(channel.netQuantity ?? 0) < 0 ? 'flex-end' : 'flex-start'}>
                <Box
                  width={
                    maximum === 0
                      ? '0%'
                      : `${(Math.abs(channel.netQuantity ?? 0) / maximum) * 100}%`
                  }
                  height={3}
                  backgroundColor={
                    (channel.netQuantity ?? 0) < 0
                      ? 'palettes.red.60.100'
                      : CHANNEL_APPEARANCE[channel.channel].color
                  }
                  borderRadius="small"
                />
              </HStack>
            </Box>
          </VStack>
        ))}
      </VStack>
    </Card>
  );
}
